import React, { useState } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Checkbox, CircularProgress, Alert, Chip, IconButton, Tooltip,
} from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DirectionToggle } from './activityDirection';
import { readEmailFile, EMAIL_FILE_ACCEPT } from './emailFileText';
import { parseEmailThread } from './emailThreadAI';

/** Motparten hos kunden är den av parterna som inte är TIKAB. */
export function counterpartOf(msg, direction) {
  if (direction === 'in') return { name: msg.fromName || '', email: msg.fromEmail || '' };
  return { name: msg.toNames?.[0] || '', email: msg.toEmails?.[0] || '' };
}

/**
 * Läser av en mailkonversation och loggar varje mail som en egen aktivitet.
 * Man hinner granska riktning och urval innan något skrivs till leadet —
 * modellen träffar rätt för det mesta, men en felvänd pil i loggen är värre
 * än ett klick extra här.
 */
export default function EmailThreadImportDialog({
  open, onClose, company, contacts = [], currentUser, userProfile, onImport,
}) {
  const [text, setText]           = useState('');
  const [fileNames, setFiles]     = useState([]);
  const [reading, setReading]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [rows, setRows]           = useState(null);   // null = tråden är inte avläst än
  const [expanded, setExpanded]   = useState(new Set());
  const [importing, setImporting] = useState(false);

  function reset() {
    setText(''); setFiles([]); setRows(null); setError('');
    setExpanded(new Set()); setLoading(false); setImporting(false);
  }

  function handleClose() {
    if (loading || importing) return;
    reset();
    onClose();
  }

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setReading(true);
    setError('');
    try {
      const parts = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const content = await readEmailFile(file);
        parts.push(`--- ${file.name} ---\n${content}`);
      }
      setFiles(prev => [...prev, ...files.map(f => f.name)]);
      setText(prev => [prev, ...parts].filter(Boolean).join('\n\n'));
    } catch (err) {
      setError(`Kunde inte läsa filen: ${err.message}`);
    } finally {
      setReading(false);
    }
  }

  async function handleAnalyse() {
    if (!text.trim()) return;
    setLoading(true);
    setError('');
    try {
      const messages = await parseEmailThread({
        text: text.trim(),
        ourEmail: currentUser?.email || userProfile?.email || '',
        ourName: userProfile?.name || currentUser?.displayName || '',
        customerName: company?.name || '',
        contacts,
      });
      if (!messages.length) {
        setError('Hittade inga mail i texten. Kontrollera att hela tråden kom med.');
      }
      setRows(messages.map(m => ({ ...m, include: true })));
    } catch (err) {
      console.error(err);
      setError(`Kunde inte läsa av tråden${err?.message ? `: ${err.message}` : ''}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleImport() {
    const selected = (rows || []).filter(r => r.include);
    if (!selected.length) return;
    setImporting(true);
    try {
      await onImport(selected);
      reset();
      onClose();
    } catch (err) {
      console.error(err);
      setError(`Importen misslyckades${err?.message ? `: ${err.message}` : ''}`);
      setImporting(false);
    }
  }

  function updateRow(idx, patch) {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function toggleExpanded(idx) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }

  const selectedCount = (rows || []).filter(r => r.include).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ForumIcon sx={{ color: '#1565C0', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">Importera mailtråd</Typography>
            <Typography variant="caption" color="text.secondary">
              Varje mail i tråden loggas som en egen aktivitet
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        {rows === null ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth multiline minRows={10} maxRows={20}
              label="Klistra in mailtråden"
              placeholder={'Markera hela konversationen i Outlook och klistra in här.\n\nEller ladda upp mailen som filer nedan.'}
              value={text}
              onChange={e => setText(e.target.value)}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.78rem' } }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button component="label" size="small" variant="outlined"
                startIcon={reading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                disabled={reading}
                sx={{ borderColor: '#1565C0', color: '#1565C0' }}>
                {reading ? 'Läser filer...' : 'Ladda upp mailfiler'}
                <input hidden multiple type="file" accept={EMAIL_FILE_ACCEPT} onChange={handleFiles} />
              </Button>
              <Typography variant="caption" color="text.secondary">
                .eml, .msg, .txt eller .html — flera samtidigt går bra
              </Typography>
              {fileNames.map((n, i) => (
                <Chip key={i} label={n} size="small"
                  sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#E3F2FD', color: '#1565C0' }} />
              ))}
            </Box>

            <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
              Signaturer, friskrivningar och citerad historik rensas bort. Datum, avsändare
              och riktning fylls i automatiskt — du får granska allt innan något sparas.
              Mail sparade som <strong>.msg</strong> läses av på bästa förmåga; blir resultatet
              rörigt, klistra in texten i stället.
            </Alert>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {rows.length} mail hittades — {selectedCount} kryssade för import
              </Typography>
              <Button size="small" onClick={() => { setRows(null); setError(''); }}>
                Tillbaka till texten
              </Button>
            </Box>

            {rows.map((row, idx) => {
              const other = counterpartOf(row, row.direction);
              const isOpen = expanded.has(idx);
              return (
                <Box key={idx} sx={{
                  border: '1px solid', borderColor: row.include ? '#BBDEFB' : 'divider',
                  bgcolor: row.include ? '#F8FBFF' : '#fafafa',
                  borderRadius: '10px', p: 1.5, opacity: row.include ? 1 : 0.6,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Checkbox size="small" checked={row.include} sx={{ p: 0.5, mt: -0.25 }}
                      onChange={e => updateRow(idx, { include: e.target.checked })} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <DirectionToggle dense label=""
                          direction={row.direction}
                          counterpartLabel={other.name || other.email}
                          onChange={dir => updateRow(idx, { direction: dir })} />
                        <Typography variant="caption" color="text.disabled">
                          {row.date || 'datum saknas'}{row.time ? ` ${row.time}` : ''}
                        </Typography>
                      </Box>
                      {row.subject && (
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.75 }}>
                          {row.subject}
                        </Typography>
                      )}
                      {row.summary && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {row.summary}
                        </Typography>
                      )}
                      {row.body && (
                        <Box sx={{ mt: 0.75 }}>
                          <Tooltip title={isOpen ? 'Dölj brödtext' : 'Visa brödtext'}>
                            <IconButton size="small" onClick={() => toggleExpanded(idx)}>
                              {isOpen
                                ? <ExpandLessIcon sx={{ fontSize: 16 }} />
                                : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                          {isOpen && (
                            <Box sx={{
                              mt: 0.5, p: 1.5, bgcolor: 'white', border: '1px solid #BBDEFB',
                              borderRadius: 1, maxHeight: 220, overflowY: 'auto',
                            }}>
                              <Typography variant="caption" sx={{
                                whiteSpace: 'pre-wrap', color: 'text.secondary',
                                fontSize: '0.76rem', lineHeight: 1.55,
                              }}>
                                {row.body}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading || importing}>Avbryt</Button>
        {rows === null ? (
          <Button variant="contained" onClick={handleAnalyse}
            disabled={!text.trim() || loading || reading}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <AutoFixHighIcon />}
            sx={{ bgcolor: '#1565C0', '&:hover': { bgcolor: '#0d47a1' } }}>
            {loading ? 'Läser av tråden...' : 'Läs av tråden'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleImport}
            disabled={!selectedCount || importing}
            startIcon={importing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55f00' } }}>
            {importing ? 'Sparar...' : `Logga ${selectedCount} mail`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
